import type { ReactElement } from "react";
import { IconButton, Stack, Tooltip } from "@mui/material";

export interface RowAction {
  /** Tooltip text and aria-label. These buttons are icon-only, so this is the action's only name. */
  label: string;
  icon: ReactElement;
  onClick: () => void;
  /** Why the action is unavailable. Present ⇒ the button is disabled and shows this instead of `label`. */
  disabledReason?: string;
  color?: "default" | "primary" | "error";
  /** Skip rendering entirely — use for permission gates, where a disabled button says nothing useful. */
  hidden?: boolean;
}

/**
 * The row-action cell every master listing uses: one icon button per action, in place of an
 * ellipsis that hid Edit and Delete behind a menu click.
 *
 * Put it in a column named "actions" — the app theme flexes that cell so the icons sit centred
 * in the row rather than on the cell's tall text baseline.
 */
export function RowActions({ actions }: { actions: RowAction[] }) {
  const visible = actions.filter(a => !a.hidden);
  if (visible.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.25}>
      {visible.map(a => (
        // A disabled button emits no pointer events, so the tooltip needs a wrapper to sit on.
        <Tooltip key={a.label} title={a.disabledReason || a.label}>
          <span>
            <IconButton
              size="small"
              color={a.color ?? "default"}
              aria-label={a.label}
              disabled={!!a.disabledReason}
              onClick={(e) => { e.stopPropagation(); a.onClick(); }}
            >
              {a.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Stack>
  );
}
